// Page Zone Sensible : photo + commentaire + GPS.
// Plus simple qu'un avaloir (pas de questionnaire), mais avec contexte (photo + texte).

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Camera, Check, ChevronLeft, Loader2, MapPin } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';

// Capture GPS — équivalent simplifié de captureBestPosition.
function captureBestPosition({ goodAccuracy = 20, hardTimeout = 6000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('GPS indisponible sur cet appareil'));
      return;
    }
    let best = null, resolved = false, watchId = null, timer = null;
    const finish = (val) => {
      if (resolved) return;
      resolved = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (timer) clearTimeout(timer);
      if (val) resolve(val);
      else reject(new Error("Position GPS introuvable"));
    };
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy;
        if (!best || acc < best.acc) {
          best = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc };
        }
        if (acc <= goodAccuracy) finish(best);
      },
      (err) => {
        if (best) finish(best);
        else if (err && err.code === 1) reject(new Error('Permission GPS refusée'));
        else finish(null);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: hardTimeout }
    );
    timer = setTimeout(() => finish(best), hardTimeout);
  });
}

export default function ZoneSensible() {
  const nav = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);

  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { code? } | null
  const [coords, setCoords] = useState(null);
  const [gpsState, setGpsState] = useState('idle'); // idle | locating | ok | error
  const [city, setCity] = useState(null);
  const [cityCode, setCityCode] = useState(null);

  useEffect(() => () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  // Au mount : si Home nous a passé la photo (state), on la consomme.
  // Sinon on tente d'ouvrir la caméra (peut être bloqué par iOS Safari).
  useEffect(() => {
    captureGps();
    const incoming = location.state?.photo;
    if (incoming) {
      setPhoto(incoming);
      setPhotoUrl(URL.createObjectURL(incoming));
      window.history.replaceState({}, '');
    } else {
      setTimeout(() => fileRef.current?.click(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureGps = async () => {
    setGpsState('locating');
    try {
      const pos = await captureBestPosition();
      setCoords(pos);
      setGpsState('ok');
      // reverse geocode (best-effort)
      try {
        const r = await fetch(`https://api-adresse.data.gouv.fr/reverse/?lon=${pos.lng}&lat=${pos.lat}`);
        const j = await r.json();
        const feat = j.features?.[0];
        if (feat) {
          setCity(feat.properties.city || feat.properties.municipality || null);
          setCityCode(feat.properties.citycode || null);
        }
      } catch { /* ignore */ }
    } catch (err) {
      setGpsState('error');
      setError((err && err.message) || 'GPS introuvable');
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
  };

  const onSubmit = async () => {
    setError(null);
    if (!photo) {
      setError('Photo requise');
      return;
    }
    if (!coords) {
      setError('Position GPS en attente — réessayez');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', 'zone_sensible');
      fd.append('photo', photo);
      fd.append('latitude', String(coords.lat));
      fd.append('longitude', String(coords.lng));
      fd.append('accuracy_m', String(coords.acc));
      if (city) fd.append('city', city);
      if (cityCode) fd.append('city_code', cityCode);
      if (notes.trim()) fd.append('notes', notes.trim());
      const res = await api.postForm('/api/quick-points', fd);
      setSuccess({ code: res?.code || null });
      setTimeout(() => nav('/', { replace: true }), 1600);
    } catch (err) {
      setError((err && err.message) || 'Échec de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="zs">
        <Header />
        <main className="zs__main zs__main--center">
          <div className="zs__success">
            <div className="zs__success-check"><Check size={48} strokeWidth={3} /></div>
            <h2 className="zs__success-title">Zone sensible enregistrée</h2>
            {success.code && (
              <div className="zs__success-code">{success.code}</div>
            )}
            <p className="zs__success-msg">Bien enregistrée — retour à l'accueil…</p>
          </div>
        </main>
        <Footer />
        <style>{`
          .zs__main--center { justify-content: center; align-items: center; }
          .zs__success {
            display: flex; flex-direction: column; align-items: center; gap: 14px;
            background: #fff; border: 1px solid var(--vrt-border);
            border-radius: 16px; padding: 32px 28px; text-align: center;
            max-width: 360px;
          }
          .zs__success-check {
            width: 88px; height: 88px; border-radius: 50%;
            background: var(--vrt-success, #16a34a); color: #fff;
            display: flex; align-items: center; justify-content: center;
          }
          .zs__success-title { margin: 0; color: var(--vrt-navy); font-size: 20px; }
          .zs__success-code {
            display: inline-block; padding: 4px 12px;
            background: var(--vrt-bg-alt); border-radius: 999px;
            font-family: ui-monospace, monospace; font-weight: 700;
            color: var(--vrt-navy); font-size: 14px; letter-spacing: 0.05em;
          }
          .zs__success-msg { color: var(--vrt-text-muted); font-size: 14px; margin: 0; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="zs">
      <Header />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />

      <main className="zs__main">
        <button type="button" className="vrt-btn vrt-btn--ghost zs__back" onClick={() => nav('/')}>
          <ChevronLeft size={18} />
          Retour
        </button>

        <div className="zs__banner">
          <AlertTriangle size={18} />
          <span>ZONE SENSIBLE</span>
        </div>

        <div className="zs__card">
          {photoUrl ? (
            <>
              <img src={photoUrl} alt="Aperçu zone sensible" className="zs__photo" />
              <button
                type="button"
                className="vrt-btn vrt-btn--ghost"
                onClick={() => fileRef.current?.click()}
                disabled={submitting}
              >
                <Camera size={18} /> Reprendre la photo
              </button>
            </>
          ) : (
            <button
              type="button"
              className="vrt-btn vrt-btn--teal zs__big"
              onClick={() => fileRef.current?.click()}
              disabled={submitting}
            >
              <Camera size={20} /> Ouvrir l'appareil photo
            </button>
          )}

          {/* GPS status */}
          <div className="zs__gps">
            <MapPin size={16} />
            {gpsState === 'locating' && <span>Localisation…</span>}
            {gpsState === 'ok' && coords && (
              <span>±{Math.round(coords.acc)} m · {city || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`}</span>
            )}
            {gpsState === 'error' && (
              <button type="button" className="zs__gps-retry" onClick={captureGps}>
                Réessayer GPS
              </button>
            )}
          </div>

          <textarea
            className="vrt-input zs__notes"
            rows={4}
            placeholder="Commentaire (optionnel) : décris la zone sensible…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            disabled={submitting}
          />

          {error && <div className="zs__error" role="alert">{error}</div>}

          <button
            type="button"
            className="vrt-btn vrt-btn--teal zs__submit"
            onClick={onSubmit}
            disabled={submitting || !photo || !coords}
          >
            {submitting
              ? <><Loader2 size={18} className="zs__spin" /> Envoi en cours…</>
              : 'Enregistrer la zone sensible'}
          </button>
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes zs-spin { to { transform: rotate(360deg); } }
        .zs__spin { animation: zs-spin 1s linear infinite; }
        .zs { min-height: 100vh; display: flex; flex-direction: column; background: var(--vrt-bg); }
        .zs__main { flex: 1; max-width: 640px; width: 100%; margin: 0 auto; padding: 16px; display: flex; flex-direction: column; gap: 14px; }
        .zs__back { align-self: flex-start; }
        .zs__banner {
          display: inline-flex; align-items: center; gap: 8px;
          background: #ef4444; color: #fff; padding: 8px 14px;
          border-radius: 999px; font-weight: 800; font-size: 14px;
          letter-spacing: 0.05em; align-self: flex-start;
          box-shadow: 0 4px 14px rgba(239, 68, 68, 0.32);
        }
        .zs__card {
          background: #fff; border: 1px solid var(--vrt-border);
          border-radius: 16px; padding: 16px;
          display: flex; flex-direction: column; gap: 14px;
        }
        .zs__photo {
          width: 100%; max-height: 320px; object-fit: cover;
          border-radius: 12px; background: #000;
        }
        .zs__big { min-height: 64px; font-size: 18px; }
        .zs__gps {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; color: var(--vrt-text-muted);
        }
        .zs__gps-retry {
          background: transparent; border: 1px solid var(--vrt-border);
          padding: 4px 10px; border-radius: 6px; cursor: pointer;
          color: var(--vrt-navy); font-size: 13px;
        }
        .zs__notes {
          width: 100%; resize: vertical; min-height: 90px;
          font-family: var(--vrt-font); font-size: 15px;
          padding: 12px; border-radius: 10px;
        }
        .zs__error {
          background: #fdecea; color: #a23123;
          padding: 10px 12px; border-radius: 8px; font-size: 14px;
        }
        .zs__submit {
          min-height: 56px; font-size: 17px; font-weight: 700;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        }
      `}</style>
    </div>
  );
}
